<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const uiFlags = useMapGetter('aifocusInboxes/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);

const connectedInboxes = useMapGetter('aifocusInboxes/getRecords');
const allInboxes = useMapGetter('inboxes/getInboxes');

const connectDialogRef = ref(null);
const disconnectDialogRef = ref(null);
const connectingInboxId = ref(null);
const disconnectingInbox = ref(null);
const isDisconnecting = ref(false);

const availableInboxes = computed(() => {
  const connectedIds = connectedInboxes.value.map(i => i.inbox_id || i.id);
  return allInboxes.value.filter(inbox => !connectedIds.includes(inbox.id));
});

onMounted(() => {
  store.dispatch('aifocusInboxes/get', { agentId: props.agentId });
  store.dispatch('inboxes/get');
});

const handleConnect = async inboxId => {
  try {
    connectingInboxId.value = inboxId;
    await store.dispatch('aifocusInboxes/create', {
      agentId: props.agentId,
      inbox_id: inboxId,
    });
    await store.dispatch('aifocusInboxes/get', { agentId: props.agentId });
    useAlert('Caixa de entrada conectada com sucesso');
    connectDialogRef.value?.close();
  } catch (error) {
    useAlert(error?.message || 'Erro ao conectar caixa de entrada');
  } finally {
    connectingInboxId.value = null;
  }
};

const openDisconnectDialog = inbox => {
  disconnectingInbox.value = inbox;
  disconnectDialogRef.value?.open();
};

const handleDisconnect = async () => {
  if (!disconnectingInbox.value) return;

  try {
    isDisconnecting.value = true;
    await store.dispatch('aifocusInboxes/delete', {
      agentId: props.agentId,
      inboxId: disconnectingInbox.value.id,
    });
    await store.dispatch('aifocusInboxes/get', { agentId: props.agentId });
    useAlert('Caixa de entrada desconectada');
    disconnectDialogRef.value?.close();
  } catch (error) {
    useAlert(error?.message || 'Erro ao desconectar');
  } finally {
    isDisconnecting.value = false;
    disconnectingInbox.value = null;
  }
};

const getInboxIcon = inbox => {
  const channelType = inbox.channel_type || inbox.channel?.type;
  const icons = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::Api': 'i-lucide-code',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return icons[channelType] || 'i-lucide-inbox';
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="pb-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <SettingsHeader
        heading="Caixas de Entrada Conectadas"
        description="Conecte caixas de entrada para que o agente responda automaticamente."
      />
      <Button
        label="Conectar Caixa de Entrada"
        icon="i-lucide-plus"
        @click="connectDialogRef?.open()"
      />
    </div>

    <!-- Loading -->
    <div v-if="isFetching" class="flex justify-center py-8">
      <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="connectedInboxes.length === 0"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <div
        class="w-16 h-16 mb-4 rounded-full bg-n-alpha-2 flex items-center justify-center"
      >
        <span class="i-lucide-inbox text-3xl text-n-slate-10" />
      </div>
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        Nenhuma caixa de entrada conectada
      </h3>
      <p class="text-sm text-n-slate-11 mb-6 max-w-md">
        Conecte uma caixa de entrada para que o agente possa responder mensagens
        automaticamente.
      </p>
      <Button
        label="Conectar Caixa de Entrada"
        icon="i-lucide-plus"
        @click="connectDialogRef?.open()"
      />
    </div>

    <!-- Connected Inboxes List -->
    <div v-else class="grid gap-3">
      <div
        v-for="inbox in connectedInboxes"
        :key="inbox.id"
        class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-n-alpha-2 flex items-center justify-center"
          >
            <span
              class="text-xl text-n-slate-11"
              :class="[getInboxIcon(inbox)]"
            />
          </div>
          <div>
            <p class="font-medium text-n-slate-12">{{ inbox.name }}</p>
            <p class="text-xs text-n-slate-10">
              {{ inbox.channel_type?.replace('Channel::', '') || 'Canal' }}
            </p>
          </div>
        </div>
        <Button
          label="Desconectar"
          variant="secondary"
          size="sm"
          color="ruby"
          @click="openDisconnectDialog(inbox)"
        />
      </div>
    </div>
  </div>

  <!-- Connect Dialog -->
  <Dialog
    ref="connectDialogRef"
    title="Conectar Caixa de Entrada"
    description="Selecione uma caixa de entrada para conectar ao agente."
    :show-confirm-button="false"
    cancel-button-label="Fechar"
    overflow-y-auto
    width="lg"
  >
    <div v-if="availableInboxes.length === 0" class="text-center py-8">
      <p class="text-n-slate-11">
        Todas as caixas de entrada ja estao conectadas.
      </p>
    </div>

    <div v-else class="grid gap-2">
      <button
        v-for="inbox in availableInboxes"
        :key="inbox.id"
        class="flex items-center gap-3 p-3 rounded-xl border border-n-weak hover:border-n-brand hover:bg-n-alpha-1 transition-colors text-left w-full"
        :disabled="connectingInboxId === inbox.id"
        @click="handleConnect(inbox.id)"
      >
        <div
          class="w-10 h-10 rounded-lg bg-n-alpha-2 flex items-center justify-center"
        >
          <span
            class="text-xl text-n-slate-11"
            :class="[getInboxIcon(inbox)]"
          />
        </div>
        <div class="flex-1">
          <p class="font-medium text-n-slate-12">{{ inbox.name }}</p>
          <p class="text-xs text-n-slate-10">
            {{ inbox.channel_type?.replace('Channel::', '') }}
          </p>
        </div>
        <span
          v-if="connectingInboxId === inbox.id"
          class="i-lucide-loader-2 animate-spin text-n-slate-11"
        />
        <span v-else class="i-lucide-plus text-n-slate-10" />
      </button>
    </div>
  </Dialog>

  <!-- Disconnect Confirmation Dialog -->
  <Dialog
    ref="disconnectDialogRef"
    type="alert"
    title="Desconectar Caixa de Entrada"
    :description="`Deseja desconectar '${disconnectingInbox?.name || ''}' deste agente?`"
    confirm-button-label="Desconectar"
    cancel-button-label="Cancelar"
    :is-loading="isDisconnecting"
    width="md"
    @confirm="handleDisconnect"
  />
</template>
